import React from 'react';
import { useAuth } from '../../context/AuthContext';
import { hasAnyCapability } from '../../utils/capabilities';
import Layout from '../layout/Layout';
import { Loader2 } from 'lucide-react';
import NotFoundPage from '../../pages/NotFoundPage';

/**
 * AdminRoute — Silent-404 guard for admin + sub-admin pages.
 *
 * Any unauthorized visitor (logged-out, regular user, or sub-admin missing
 * the required capability) sees a plain 404, identical to a URL that doesn't
 * exist. This prevents URL enumeration of the admin surface.
 *
 * Props:
 *   - anyCap: optional capability list; sub-admins must hold at least one.
 *             Full admins bypass this.
 *
 * Race-condition safety: while `authLoading` is true, a spinner is rendered
 * so a legitimate admin's page does NOT flash 404 on hard refresh before the
 * /auth/me call resolves.
 */
const AdminRoute = ({ children, anyCap = null }) => {
  const { user, authLoading } = useAuth();

  if (authLoading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-6 max-w-4xl">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#0A7EA4]" data-testid="admin-route-loading" />
            <span className="ml-3 text-gray-600">Loading...</span>
          </div>
        </div>
      </Layout>
    );
  }

  const isAdminOrSubAdmin = user?.role === 'admin' || user?.role === 'sub_admin';

  // Logged out or wrong role → 404.
  if (!user || !isAdminOrSubAdmin) {
    return <NotFoundPage />;
  }

  // Sub-admin missing required caps → 404.
  if (user.role !== 'admin' && Array.isArray(anyCap) && anyCap.length > 0) {
    if (!hasAnyCapability(user, anyCap)) {
      return <NotFoundPage />;
    }
  }

  return children;
};

export default AdminRoute;
